import { useEffect, useState } from "react";
import { Camera, Gem, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MarginsCard } from "./MarginsCard";
import { ShotDrop } from "./ShotDrop";

const MARKET = { spread: "SPREAD", total: "TOTAL", moneyline: "MONEYLINE" };
const pct = (v) => (v == null ? "—" : `${Math.round(v * 100)}%`);
const modelText = (p) => (p.market === "moneyline" ? `${pct(p.model)} win vs ${pct(p.book_prob)} book`
  : p.market === "spread" ? `model ${p.model > 0 ? "+" : ""}${p.model}` : `model ${p.model}`);

export const ValueChip = ({ id }) => <span className="tv-vchip" data-testid={`tp-value-chip-${id}`}><Gem size={10} />VALUE</span>;

function PickCard({ p, codes }) {
  const res = p.result;
  return (
    <div className={`tv-vcard ${res || ""}`} data-testid={`tv-pick-${p.game_id || "game"}-${p.market}`}>
      <small>{MARKET[p.market]}{codes ? ` · ${codes}` : ""}</small>
      <b>{p.label}</b>
      <span className="tv-vprice">{p.price != null ? Number(p.price).toFixed(2) : "—"}</span>
      <em>{modelText(p)} · edge {p.edge_label}</em>
      {res && <i className="tv-vres" data-testid={`tv-pick-result-${p.market}`}>{res.toUpperCase()}{p.final ? ` · ${p.final}` : ""}</i>}
    </div>
  );
}

const FIELDS = [["spread_home", "{H} spread"], ["total", "Total"], ["ml_home", "{H} ML"], ["ml_away", "{A} ML"]];
const STD_PRICE = { spread_home_odds: 1.91, spread_away_odds: 1.91, over_odds: 1.91, under_odds: 1.91 };  // spread/total always priced at 1.91

function LinesEditor({ game, book, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(Object.fromEntries(FIELDS.map(([k]) => [k, book?.[k] ?? ""]))); }, [book]);
  const pick = (e) => { const files = [...e.target.files]; e.target.value = ""; if (files.length) scan(files); };
  const scan = async (files) => {
    const fd = new FormData(); files.forEach((f) => fd.append("files", f));
    setBusy(true);
    try {
      const r = await api.post(`/team-value/${game.id}/scan`, fd);
      setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(r.data.lines).filter(([k, v]) => v != null && FIELDS.some(([x]) => x === k))) }));
      toast.success("Lines read from screenshot — check and save");
      r.data.warnings.forEach((w) => toast.warning(w));
    } catch (err) { toast.error(errorText(err)); } finally { setBusy(false); }
  };
  const save = async () => {
    const body = { ...STD_PRICE, ...Object.fromEntries(Object.entries(form).filter(([, v]) => v !== "" && v != null).map(([k, v]) => [k, Number(v)])) };
    try { await api.put(`/team-value/${game.id}/lines`, body); toast.success("Lines saved"); onSaved(); } catch (err) { toast.error(errorText(err)); }
  };
  const reset = async () => {
    try { await api.delete(`/team-value/${game.id}/lines`); toast.success("Back to ESPN lines"); onSaved(); } catch (err) { toast.error(errorText(err)); }
  };
  return (
    <ShotDrop onFiles={scan} busy={busy} testId="tv-lines-drop">
    <div className="tp-card" data-testid="tv-lines-editor">
      <h4>DRAFTKINGS LINES <small>(admin · used for value picks · spread &amp; total priced at 1.91)</small></h4>
      <div className="tv-vform">{FIELDS.map(([k, label]) => (
        <label key={k}><small>{label.replace("{H}", game.home_code).replace("{A}", game.away_code)}</small><input value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`tv-line-${k}`} /></label>
      ))}</div>
      <div className="tv-vactions">
        <label className="tv-vbtn" data-testid="tv-lines-scan"><Camera size={14} />{busy ? "Reading…" : "Scan screenshot"}
          <input type="file" accept="image/png,image/jpeg,image/webp" multiple hidden onChange={pick} disabled={busy} data-testid="tv-lines-file" /></label>
        <button type="button" className="tv-vbtn primary" onClick={save} data-testid="tv-lines-save"><Save size={14} />Save lines</button>
        <button type="button" className="tv-vbtn" onClick={reset} data-testid="tv-lines-reset"><RotateCcw size={14} />Use ESPN</button>
      </div>
    </div>
    </ShotDrop>
  );
}

function Record({ rec }) {
  const cells = [["all", "ALL"], ["spread", "SPREAD"], ["total", "TOTAL"], ["moneyline", "ML"]];
  return (
    <div className="tp-card" data-testid="tv-record">
      <h4>TRACK RECORD <small>(settled picks · pregame + live)</small></h4>
      <div className="tv-vrec">{cells.map(([k, t]) => (
        <div key={k} data-testid={`tv-record-${k}`}><small>{t}</small><b>{pct(rec[k].hit_rate)}</b>
          <em>{rec[k].wins}-{rec[k].losses}{rec[k].pushes ? `-${rec[k].pushes}` : ""} · {rec[k].units > 0 ? "+" : ""}{rec[k].units}u</em></div>
      ))}</div>
    </div>
  );
}

function RecentPicks({ rows }) {
  if (!rows?.length) return null;
  return (
    <div className="tp-card" data-testid="tv-recent">
      <h4>RECENT SETTLED PICKS <small>(newest first · LIVE = half-time team scan)</small></h4>
      <div className="tv-recent-list">
        {rows.map((p) => (
          <div key={p.id || `${p.game_id}-${p.market}-${p.phase}`} className={`tv-recent-row ${p.result || ""}`} data-testid={`tv-recent-${p.game_id}-${p.market}`}>
            <span className={`tv-phase ${p.phase}`}>{p.phase === "live" ? "LIVE" : "PRE"}</span>
            <span className="tv-recent-match">{p.away_code} @ {p.home_code}</span>
            <span className="tv-recent-pick"><em>{MARKET[p.market]}</em> {p.label}</span>
            <span className="tv-recent-price">{p.price != null ? Number(p.price).toFixed(2) : "—"}</span>
            {p.is_value && <ValueChip id={`${p.game_id}-${p.market}`} />}
            {p.result && <i className={`tv-recent-res ${p.result}`}>{p.result.toUpperCase()}{p.final ? ` · ${p.final}` : ""}</i>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function TeamValueTab({ game, value, book, day, onSelect, reload }) {
  const { user } = useAuth();
  const [dayPicks, setDayPicks] = useState(null);
  const [hist, setHist] = useState(null);
  useEffect(() => {
    api.get("/team-value", { params: { day } }).then((r) => setDayPicks(r.data.picks)).catch(() => setDayPicks([]));
    api.get("/team-value/history").then((r) => setHist(r.data)).catch(() => {});
  }, [day, value]);
  const th = value.thresholds;
  return (
    <>
      <div className="tp-card" data-testid="tv-game-picks">
        <h4>VALUE PICKS · THIS GAME <small>({value.source === "screenshot" ? "DraftKings screenshot" : value.source === "espn" ? "DraftKings via ESPN" : "no line yet"})</small></h4>
        {value.picks.length ? <div className="tv-vgrid">{value.picks.map((p) => <PickCard key={p.market} p={p} />)}</div>
          : <p className="tp-muted" data-testid="tv-no-picks">{value.source ? "The model is within the margin of every line for this game." : "No DraftKings line for this game yet."}</p>}
        <p className="tp-note">Flagged when the model beats the line by spread ≥ {th.spread} pts, total ≥ {th.total} pts, or moneyline win chance ≥ {Math.round(th.moneyline * 100)}% above the book&apos;s. Picks lock at tip-off and are graded at the final.</p>
      </div>
      {user?.role === "admin" && game.status === "pre" && <LinesEditor game={game} book={book} onSaved={reload} />}
      {user?.role === "admin" && <MarginsCard compact onSaved={reload} />}
      <div className="tp-card" data-testid="tv-day-picks">
        <h4>ALL VALUE PICKS · TODAY&apos;S SLATE</h4>
        {dayPicks === null && <p className="tp-muted">Loading…</p>}
        {dayPicks?.length === 0 && <p className="tp-muted">No value picks on this slate.</p>}
        <div className="tv-vgrid">{dayPicks?.map((p) => (
          <button type="button" key={`${p.game_id}-${p.market}`} className="tv-vlink" onClick={() => onSelect?.(p.game_id)}><PickCard p={p} codes={`${p.away_code} @ ${p.home_code}`} /></button>
        ))}</div>
      </div>
      {hist && <Record rec={hist.record} />}
      {hist && <RecentPicks rows={hist.recent} />}
    </>
  );
}
